// components/ui/undo-toast.tsx
import React, { useEffect, useState } from "react"
import { X } from "lucide-react"

interface UndoToastProps {
    message: string
    duration?: number
    /** Omit to render without an Undo button — for truly-destructive completions. */
    onUndo?: () => void
    onClose: () => void
    /** Distance from the bottom edge, in px. Lets a stack of toasts sit
     *  one above the other instead of overlapping. Defaults to 24 (the
     *  lone-toast resting position). */
    stackOffset?: number
}

export const UndoToast: React.FC<UndoToastProps> = ({
    message,
    duration = 5000,
    onUndo,
    onClose,
    stackOffset = 24
}) => {
    const [isExiting, setIsExiting] = useState(false)

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsExiting(true)
            setTimeout(onClose, 300)
        }, duration)

        return () => clearTimeout(timer)
    }, [duration, onClose])

    const handleClose = () => {
        setIsExiting(true)
        setTimeout(onClose, 300)
    }

    const handleUndo = () => {
        onUndo?.()
        setIsExiting(true)
        setTimeout(onClose, 300)
    }

    return (
        <div
            style={{
                left: "calc(var(--nav-width, 192px) + 16px)",
                bottom: stackOffset,
                maxWidth: "min(560px, calc(100vw - var(--nav-width, 192px) - 48px))",
            }}
            className={`
                fixed z-[9999]
                bg-[#2E2524] rounded-md shadow-lg
                transition-[left,bottom,opacity,transform] duration-300 ease-in-out
                ${isExiting ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}
            `}
        >
            <div className="flex items-center gap-3 pl-3 pr-2 py-2">
                <p className="min-w-0 flex-1 truncate text-white text-[13px]" title={message}>{message}</p>
                {onUndo && (
                    <button
                        onClick={handleUndo}
                        className="shrink-0 text-white text-[13px] font-medium hover:underline px-1.5 py-0.5"
                    >
                        Undo
                    </button>
                )}
                <button
                    onClick={handleClose}
                    className="shrink-0 text-white/70 hover:text-white transition-colors p-1"
                    aria-label="Close"
                >
                    <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
            </div>
        </div>
    )
}
